// pages/UserAdmin.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@components/Layout';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

import { auth, db } from '@root/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// ---- Config ----
const FUNCTIONS_BASE = (import.meta.env.VITE_FUNCTIONS_URL || '').replace(
  /\/$/,
  ''
);
const PAGE_SIZE_DEFAULT = 10;

/* ======================================================================================
 * Inline callFn with rich logging
 * ====================================================================================*/
async function callFn(
  path,
  body = {},
  { label, log = true, timeoutMs = 20000 } = {}
) {
  if (!auth.currentUser) {
    const err = new Error('Not authenticated');
    if (log) console.error('[fn] no currentUser for', path);
    throw err;
  }

  const cleanPath = String(path || '').replace(/^\//, '');
  const url = `${FUNCTIONS_BASE}/${cleanPath}`;
  const reqId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
  const started = Date.now();

  if (log) {
    console.groupCollapsed?.(
      `%c[fn] ${label || cleanPath} #${reqId}`,
      'color:#00B7F4;font-weight:700'
    );
    console.log('→ URL:', url);
    console.log('→ Path:', cleanPath);
    console.log('→ Body:', body);
  }

  let res, text, json;
  try {
    const idToken = await auth.currentUser.getIdToken(true);

    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body || {}),
      ...(controller ? { signal: controller.signal } : {}),
    });

    if (timer) clearTimeout(timer);

    text = await res.text();
    try {
      json = text ? JSON.parse(text) : {};
    } catch {
      json = null;
    }

    if (log) {
      console.log('← Status:', res.status, res.statusText);
      if (text) console.log('← Raw:', text);
      if (json) console.log('← JSON:', json);
    }

    if (!res.ok || (json && json.ok === false)) {
      const msg =
        (json && (json.error || json.message)) || `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.path = cleanPath;
      err.response = json ?? text;
      throw err;
    }

    return json ?? {};
  } catch (err) {
    if (log)
      console.error(`✖ [fn] ${label || cleanPath} failed #${reqId}`, err);
    throw err;
  } finally {
    if (log) {
      console.log('⏱ Duration:', Date.now() - started, 'ms');
      console.groupEnd?.();
    }
  }
}

/* ======================================================================================
 * Helpers
 * ====================================================================================*/
async function ensureAdminClaimFresh() {
  console.log(`Current Auth User: ${auth.currentUser}`);
  if (!auth.currentUser) {
    console.log('Setting Admin to False');
    return { isAdmin: false, claims: {} };
  }
  await auth.currentUser.getIdToken(true);
  const res = await auth.currentUser.getIdTokenResult();
  return { isAdmin: !!res.claims?.isAdmin, claims: res.claims };
}

async function assertAdminOrExplain() {
  console.log(`*** assertAdminOrExplain ***`);
  const { isAdmin, claims } = await ensureAdminClaimFresh();
  console.log(`isAdmin: ${isAdmin}`);
  console.log(`Claims: ${JSON.stringify(claims)}`);
  if (!isAdmin) {
    console.warn('[admin] token claims missing isAdmin:', claims);
    toast.error(
      'Your admin access is not active on this session. Please sign out/in.'
    );
  }
  return isAdmin;
}

function formatJoined(ts) {
  if (!ts) return '—';
  const d =
    ts?.toDate?.() ||
    (ts?.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
  return isNaN(d?.getTime?.()) ? '—' : d.toLocaleDateString();
}

/* ======================================================================================
 * Component
 * ====================================================================================*/
export default function UserAdmin() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [claims, setClaims] = useState({ isAdmin: false });
  const navigate = useNavigate();

  // Load users
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const c = await ensureAdminClaimFresh();
        setClaims({ isAdmin: c.isAdmin });

        if (!c.isAdmin) {
          toast.error('You need admin access to view all users.');
        }

        const base = collection(db, 'users');
        const qRef = query(base, orderBy('joinedDate', 'desc'));
        const snap = await getDocs(qRef);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(data);
      } catch (e) {
        console.error('Error fetching users:', e);
        toast.error('Failed to fetch users.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(users.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return users.slice(start, start + pageSize);
  }, [users, page, pageSize]);

  // Actions
  const onView = (uid) => navigate(`/app/users/${uid}`);

  const onToggleAdmin = async (user) => {
    const wantAdmin = !user.isAdmin;
    const res = await Swal.fire({
      title: wantAdmin ? 'Make admin?' : 'Remove admin?',
      text: `User: ${user.id}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: wantAdmin ? 'Yes, make admin' : 'Yes, remove admin',
      cancelButtonText: 'Cancel',
    });
    if (!res.isConfirmed) return;

    const ok = await assertAdminOrExplain();
    if (!ok) return;

    try {
      setFetching(true);

      await callFn(
        '/admin/setAdminClaim',
        { uid: user.id, isAdmin: wantAdmin },
        { label: 'setAdminClaim' }
      );
      await callFn(
        '/admin/updateUserDoc',
        { uid: user.id, patch: { isAdmin: wantAdmin } },
        { label: 'updateUserDoc' }
      ).catch(() => null);

      if (auth.currentUser?.uid === user.id) {
        await ensureAdminClaimFresh();
      }

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isAdmin: wantAdmin } : u))
      );
      toast.success(wantAdmin ? 'User is now admin.' : 'Admin removed.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update admin status.');
    } finally {
      setFetching(false);
    }
  };

  const onDelete = async (user) => {
    if (auth.currentUser?.uid === user.id) {
      toast.error("You can't delete your own account.");
      return;
    }

    const ok = await assertAdminOrExplain();
    if (!ok) return;

    const pre = await callFn(
      '/admin/precheckUserDeletion',
      { uid: user.id },
      { label: 'precheckUserDeletion' }
    ).catch((e) => {
      console.error(e);
      toast.error('Precheck failed.');
      return null;
    });
    if (!pre) return;

    const html = `<div style="text-align:left">
      <p><b>User:</b> ${user.id}</p>
      <p><b>Auth:</b> ${pre.auth?.exists ? 'exists' : 'not found'}</p>
      <p><b>Firestore doc:</b> ${pre.firestore?.exists ? 'exists' : 'not found'}</p>
      ${
        pre.firestore?.subcollections?.length
          ? `<p><b>Subcollections:</b></p><ul>${pre.firestore.subcollections
              .map((sc) => `<li>${sc.path} — ${sc.count} doc(s)</li>`)
              .join('')}</ul>`
          : ''
      }
      <p><b>Storage prefix:</b> ${pre.storage?.prefix} (${pre.storage?.count} file(s))</p>
    </div>`;

    const res = await Swal.fire({
      title: 'Delete user and all data?',
      html,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      focusCancel: true,
      width: 600,
    });
    if (!res.isConfirmed) return;

    try {
      setFetching(true);
      const del = await callFn(
        '/admin/deleteUser',
        { uid: user.id },
        { label: 'deleteUser', timeoutMs: 120000 }
      );
      const r = del.result?.steps || {};
      await Swal.fire({
        title: 'Deletion complete',
        html: `<div style="text-align:left">
          <p><b>Auth:</b> ${r.auth}</p>
          <p><b>Storage:</b> ${r.storage}</p>
          <p><b>Firestore:</b> ${r.firestore}</p>
        </div>`,
        icon: 'success',
      });
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      toast.success('User deleted.');
    } catch (e) {
      console.error(e);
      toast.error('Delete failed.');
    } finally {
      setFetching(false);
    }
  };

  return (
    <Layout isAdminPage>
      <div className='row'>
        <div className='col-lg-12'>
          <div className='card'>
            <div className='card-header d-flex align-items-center justify-content-between'>
              <div>
                <h4 className='card-title mb-0'>Registered Users</h4>
                <p className='card-title-desc mb-0'>
                  All accounts in the system.
                </p>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <select
                  className='form-select'
                  style={{ width: 120 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {[10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='card-body'>
              <div className='table-responsive'>
                {loading ? (
                  <p>Loading users…</p>
                ) : users.length === 0 ? (
                  <p>No users found.</p>
                ) : (
                  <>
                    <table className='table mb-0'>
                      <thead>
                        <tr>
                          <th style={{ width: 80 }}>#</th>
                          <th>User ID</th>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Joined</th>
                          <th>Admin</th>
                          <th style={{ width: 80 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((u, idx) => (
                          <tr key={u.id}>
                            <td>{(page - 1) * pageSize + idx + 1}</td>
                            <td
                              className='text-truncate'
                              style={{ maxWidth: 240 }}
                            >
                              {u.id}
                            </td>
                            <td>{u.name || '—'}</td>
                            <td>{u.email || '—'}</td>
                            <td>{formatJoined(u.joinedDate)}</td>
                            <td>
                              <span
                                className={`badge ${u.isAdmin ? 'bg-success' : 'bg-secondary'}`}
                              >
                                {u.isAdmin ? 'Admin' : 'User'}
                              </span>
                            </td>
                            <td className='text-end'>
                              <div className='dropdown'>
                                <button
                                  className='btn btn-sm btn-light dropdown-toggle'
                                  type='button'
                                  data-bs-toggle='dropdown'
                                  aria-expanded='false'
                                  disabled={fetching}
                                >
                                  ⋯
                                </button>
                                <ul className='dropdown-menu dropdown-menu-end'>
                                  <li>
                                    <button
                                      className='dropdown-item'
                                      onClick={() => onView(u.id)}
                                    >
                                      View
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className='dropdown-item'
                                      onClick={() => onToggleAdmin(u)}
                                      disabled={fetching}
                                    >
                                      {u.isAdmin
                                        ? 'Remove Admin'
                                        : 'Make Admin'}
                                    </button>
                                  </li>
                                  <li>
                                    <hr className='dropdown-divider' />
                                  </li>
                                  <li>
                                    <button
                                      className='dropdown-item text-danger'
                                      onClick={() => onDelete(u)}
                                      disabled={
                                        fetching ||
                                        auth.currentUser?.uid === u.id
                                      }
                                      title={
                                        auth.currentUser?.uid === u.id
                                          ? "You can't delete yourself"
                                          : 'Delete'
                                      }
                                    >
                                      Delete
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className='d-flex justify-content-between align-items-center mt-3'>
                      <button
                        className='btn btn-primary'
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {page} of {totalPages}
                      </span>
                      <button
                        className='btn btn-primary'
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={page === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </div>

              {!claims.isAdmin && (
                <div className='alert alert-warning mt-3' role='alert'>
                  You might not have admin access yet. If you were just
                  promoted, please sign out and back in to refresh your
                  permissions.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
